import {
  minutes,
  type CalendarId,
  type ISODate,
  type ISODateTime,
  type Minutes,
} from '../domain/branded.js';
import type { Calendar, WeekDays, WorkingTime } from '../domain/calendar.js';
import { EntityNotFoundError, InvalidArgumentError, InvalidCalendarError } from '../domain/errors.js';
import {
  compileException,
  exceptionCoversDay,
  exceptionWorkingTimes,
  type CompiledException,
} from './calendar-exceptions.js';
import {
  civilFromDays,
  formatDate,
  formatDateTime,
  MINUTES_PER_DAY,
  parseDate,
  parseDateTime,
  weekDayOf,
  type DayMinute,
} from './calendar-time.js';

/**
 * 日曆運算契約。實作者：calendar agent。
 * 這是排程、撫平、成本三個引擎的共同底層，所有時間位移都必須經由此處。
 *
 * 語義（對齊 MS Project）：
 * - 工作時段以 `[start, end)` 半開區間定義「工作時刻」，但任務 finish 落在
 *   `end` 時視為合法的結束點，不會被推到下一個工作日開始。
 * - `addWorkingMinutes(cal, t, 0)` 一律回傳 `t` 本身（里程碑不位移）。
 * - `addWorkingMinutes` 在 duration > 0 時，會先把起點對齊到下一個工作時刻。
 * - 時間運算的粒度是「分鐘」，輸入字串中的秒數會被捨去。
 * - `WorkingTime` 不得跨午夜；夜班班別必須拆成相鄰兩天的兩個時段
 *   （例：週一 `{1320, 1440}` + 週二 `{0, 360}` 表示 22:00–翌日 06:00）。
 * - weekDays 為三態（工作／明確非工作／未指定往 base 繼承），詳見 `WeekDaySpec`。
 */

/** 已解析（含繼承與例外）的日曆，供高頻查詢使用。 */
export interface ResolvedCalendar {
  readonly id: CalendarId;
  /** 查詢某日的工作時段（已套用例外與 base calendar 繼承）。 */
  workingTimesOn(date: ISODate): readonly WorkingTime[];
  /** 該日是否為工作日。 */
  isWorkingDay(date: ISODate): boolean;
  /** 該時間點是否落在工作時段內。 */
  isWorkingTime(at: ISODateTime): boolean;
}

/**
 * 建立多個日曆的共同工作時間視圖。所有日曆皆必須工作才視為可排程；
 * 用於 task/project calendar 與已指派 Work resource calendars 的交集。
 */
export function intersectWorkingCalendars(calendars: readonly ResolvedCalendar[]): ResolvedCalendar {
  if (calendars.length === 0) throw new InvalidArgumentError('intersectWorkingCalendars() requires at least one calendar');
  if (calendars.length === 1) return calendars[0]!;
  const cache = new Map<string, readonly WorkingTime[]>();
  const intersect = (date: ISODate): readonly WorkingTime[] => {
    const key = date as string;
    const cached = cache.get(key);
    if (cached !== undefined) return cached;
    let current = calendars[0]!.workingTimesOn(date);
    for (let index = 1; index < calendars.length && current.length > 0; index += 1) {
      const next = calendars[index]!.workingTimesOn(date);
      const overlap: WorkingTime[] = [];
      let left = 0;
      let right = 0;
      while (left < current.length && right < next.length) {
        const a = current[left]!;
        const b = next[right]!;
        const start = Math.max(a.start as number, b.start as number);
        const end = Math.min(a.end as number, b.end as number);
        if (start < end) overlap.push({ start: minutes(start), end: minutes(end) });
        if ((a.end as number) <= (b.end as number)) left += 1;
        else right += 1;
      }
      current = overlap;
    }
    cache.set(key, current);
    return current;
  };
  return {
    id: calendars[0]!.id,
    workingTimesOn: intersect,
    isWorkingDay: (date) => intersect(date).length > 0,
    isWorkingTime: (at) => {
      const point = parseDateTime(at);
      return intersect(formatDate(point.day)).some((segment) =>
        point.minute >= (segment.start as number) && point.minute < (segment.end as number));
    },
  };
}

// ---------------------------------------------------------------------------
// 內部結構
// ---------------------------------------------------------------------------

/** 單日解析結果（含當日工作分鐘總數，供區間加總走捷徑）。 */
interface DayInfo {
  readonly segments: readonly WorkingTime[];
  readonly total: number;
}

const EMPTY_DAY: DayInfo = { segments: [], total: 0 };

/** 繼承鏈上的一層，index 0 為最衍生（子）日曆。 */
interface CalendarLevel {
  readonly weekDays: WeekDays;
  readonly exceptions: readonly CompiledException[];
}

/**
 * 連續非工作日的容忍上限。超過即視為日曆定義有問題（例如七天皆為空、
 * 或例外把整段時間都設為非工作），避免時間位移函式陷入無限迴圈。
 */
const MAX_NON_WORKING_DAYS = 366 * 20;

/**
 * 單次區間加總可跨越的最大天數（約 100 年）。
 * 迴圈本身是有界的，這道上限是為了讓明顯離譜的輸入（例如解析錯誤造成的
 * 9999 年）立刻失敗，而不是空轉數百萬次。
 */
const MAX_SPAN_DAYS = 366 * 100;

const totalOf = (segments: readonly WorkingTime[]): number => {
  let total = 0;
  for (const segment of segments) total += (segment.end as number) - (segment.start as number);
  return total;
};

function validateWorkingTimes(
  calendarId: string,
  label: string,
  segments: readonly WorkingTime[],
): void {
  let previousEnd = -1;
  for (const segment of segments) {
    const start = segment.start as number;
    const end = segment.end as number;
    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      throw new InvalidCalendarError(calendarId, `${label} has a non-finite working time`);
    }
    if (start < 0 || end > MINUTES_PER_DAY) {
      throw new InvalidCalendarError(
        calendarId,
        `${label} working time ${start}-${end} is outside 0..${MINUTES_PER_DAY}`,
      );
    }
    if (end <= start) {
      throw new InvalidCalendarError(
        calendarId,
        `${label} working time ${start}-${end} must have end > start`,
      );
    }
    if (start < previousEnd) {
      throw new InvalidCalendarError(
        calendarId,
        `${label} working times must be sorted by start and must not overlap`,
      );
    }
    previousEnd = end;
  }
}

class ResolvedCalendarImpl implements ResolvedCalendar {
  readonly id: CalendarId;
  private readonly levels: readonly CalendarLevel[];
  private readonly cache = new Map<number, DayInfo>();

  constructor(id: CalendarId, levels: readonly CalendarLevel[]) {
    this.id = id;
    this.levels = levels;
  }

  /** 解析某一天（距離 1970-01-01 的天數）的工作時段，結果會被快取。 */
  dayInfo(day: number): DayInfo {
    const cached = this.cache.get(day);
    if (cached !== undefined) return cached;

    const civil = civilFromDays(day);
    let segments: readonly WorkingTime[] | null = null;

    // 例外優先於 weekday 預設；子日曆的例外優先於父日曆。
    outer: for (const level of this.levels) {
      for (const rule of level.exceptions) {
        if (exceptionCoversDay(rule, day, civil)) {
          segments = exceptionWorkingTimes(rule.source);
          break outer;
        }
      }
    }

    if (segments === null) {
      const weekDay = weekDayOf(day);
      // 三態：working=true 取其時段、working=false 明確非工作日、null 往上一層繼承。
      for (const level of this.levels) {
        const spec = level.weekDays[weekDay];
        if (spec === undefined || spec === null) continue;
        segments = spec.working ? spec.times : [];
        break;
      }
    }

    const info: DayInfo =
      segments === null || segments.length === 0
        ? EMPTY_DAY
        : { segments, total: totalOf(segments) };
    this.cache.set(day, info);
    return info;
  }

  workingTimesOn(date: ISODate): readonly WorkingTime[] {
    return this.dayInfo(parseDate(date)).segments;
  }

  isWorkingDay(date: ISODate): boolean {
    return this.dayInfo(parseDate(date)).total > 0;
  }

  isWorkingTime(at: ISODateTime): boolean {
    const point = parseDateTime(at);
    for (const segment of this.dayInfo(point.day).segments) {
      if (point.minute >= (segment.start as number) && point.minute < (segment.end as number)) {
        return true;
      }
    }
    return false;
  }
}

/**
 * 取得可做每日快取查詢的內部視圖。
 * 若傳入的是本模組建立的實例就直接使用；否則用公開 API 包一層快取轉接器，
 * 讓外部自訂的 ResolvedCalendar 也能享有相同效能特性。
 */
const adapters = new WeakMap<ResolvedCalendar, (day: number) => DayInfo>();

function dayLookup(calendar: ResolvedCalendar): (day: number) => DayInfo {
  if (calendar instanceof ResolvedCalendarImpl) {
    return (day) => calendar.dayInfo(day);
  }
  const existing = adapters.get(calendar);
  if (existing !== undefined) return existing;
  const cache = new Map<number, DayInfo>();
  const lookup = (day: number): DayInfo => {
    const cached = cache.get(day);
    if (cached !== undefined) return cached;
    const segments = calendar.workingTimesOn(formatDate(day));
    const info: DayInfo =
      segments.length === 0 ? EMPTY_DAY : { segments, total: totalOf(segments) };
    cache.set(day, info);
    return info;
  };
  adapters.set(calendar, lookup);
  return lookup;
}

// ---------------------------------------------------------------------------
// 解析
// ---------------------------------------------------------------------------

/** 將 Calendar 定義（含繼承鏈）解析為可查詢物件。 */
export function resolveCalendar(
  calendar: Calendar,
  allCalendars: ReadonlyMap<string, Calendar>,
): ResolvedCalendar {
  const levels: CalendarLevel[] = [];
  const chain: string[] = [];
  const seen = new Set<string>();

  let current: Calendar | undefined = calendar;
  while (current !== undefined) {
    const currentId = current.id as string;
    if (seen.has(currentId)) {
      throw new InvalidCalendarError(
        calendar.id as string,
        `circular base calendar inheritance: ${[...chain, currentId].join(' -> ')}`,
      );
    }
    seen.add(currentId);
    chain.push(currentId);

    if (current.weekDays.length !== 7) {
      throw new InvalidCalendarError(currentId, 'weekDays must contain exactly 7 entries');
    }
    for (let weekDay = 0; weekDay < 7; weekDay += 1) {
      const spec = current.weekDays[weekDay];
      if (spec === undefined || spec === null || !spec.working) continue;
      if (spec.times.length === 0) {
        throw new InvalidCalendarError(
          currentId,
          `weekDay[${weekDay}] is marked working but has no working times`,
        );
      }
      validateWorkingTimes(currentId, `weekDay[${weekDay}]`, spec.times);
    }
    const exceptions: CompiledException[] = [];
    for (const exception of current.exceptions) {
      validateWorkingTimes(currentId, `exception ${exception.id}`, exception.workingTimes);
      const compiled = compileException(exception);
      if (compiled.spanDays < 0) {
        throw new InvalidCalendarError(currentId, `exception ${exception.id} has to < from`);
      }
      exceptions.push(compiled);
    }
    levels.push({ weekDays: current.weekDays, exceptions });

    const baseId: CalendarId | null = current.baseCalendarId;
    if (baseId === null) break;
    const base = allCalendars.get(baseId as string);
    if (base === undefined) {
      throw new EntityNotFoundError('CALENDAR_NOT_FOUND', baseId as string);
    }
    current = base;
  }

  return new ResolvedCalendarImpl(calendar.id, levels);
}

// ---------------------------------------------------------------------------
// 對齊（snap）
// ---------------------------------------------------------------------------

function nextWorkingPoint(
  calendar: ResolvedCalendar,
  lookup: (day: number) => DayInfo,
  from: DayMinute,
): DayMinute {
  let day = from.day;
  let minute = from.minute;
  for (let guard = 0; guard <= MAX_NON_WORKING_DAYS; guard += 1) {
    for (const segment of lookup(day).segments) {
      const start = segment.start as number;
      const end = segment.end as number;
      if (minute < end) {
        return { day, minute: minute > start ? minute : start };
      }
    }
    day += 1;
    minute = 0;
  }
  throw new InvalidCalendarError(
    calendar.id as string,
    `no working time found within ${MAX_NON_WORKING_DAYS} days after the given instant`,
  );
}

function previousWorkingPoint(
  calendar: ResolvedCalendar,
  lookup: (day: number) => DayInfo,
  from: DayMinute,
): DayMinute {
  let day = from.day;
  let minute = from.minute;
  for (let guard = 0; guard <= MAX_NON_WORKING_DAYS; guard += 1) {
    const segments = lookup(day).segments;
    for (let i = segments.length - 1; i >= 0; i -= 1) {
      const segment = segments[i];
      if (segment === undefined) continue;
      const start = segment.start as number;
      const end = segment.end as number;
      if (minute > start) {
        return { day, minute: minute < end ? minute : end };
      }
    }
    day -= 1;
    minute = MINUTES_PER_DAY;
  }
  throw new InvalidCalendarError(
    calendar.id as string,
    `no working time found within ${MAX_NON_WORKING_DAYS} days before the given instant`,
  );
}

/**
 * 把「同一個工作時刻的兩種表示法」正規化成較早的那一種。
 *
 * 工作時段是分段的，因此某些瞬間有兩種等價寫法：午休前的 12:00 與午休後的
 * 13:00 之間沒有任何工作分鐘，週五 17:00 與週一 08:00 之間也沒有。若不統一，
 * CPM 的 forward / backward pass 反覆位移後會在兩種表示法之間漂移。
 *
 * 規則（與「finish 落在時段結束點不跳段」一致）：**取較早的表示法，也就是前一段
 * 的結束點**。只在位移運算的「輸入」端套用；運算結果維持自然形式（任務 start
 * 仍會是 08:00 而不是前一個工作日的 17:00），以免 UI 顯示反直覺。
 *
 * 找不到更早的等價表示法時（例如日曆最開頭）原值回傳，絕不拋錯。
 */
function canonicalizeInstant(lookup: (day: number) => DayInfo, at: DayMinute): DayMinute {
  let atSegmentStart = false;
  for (const segment of lookup(at.day).segments) {
    if (at.minute === (segment.start as number)) {
      atSegmentStart = true;
      break;
    }
  }
  if (!atSegmentStart) return at;

  let day = at.day;
  let minute = at.minute;
  for (let guard = 0; guard <= MAX_NON_WORKING_DAYS; guard += 1) {
    const segments = lookup(day).segments;
    for (let i = segments.length - 1; i >= 0; i -= 1) {
      const segment = segments[i];
      if (segment === undefined) continue;
      const start = segment.start as number;
      const end = segment.end as number;
      if (minute > start) {
        return { day, minute: minute < end ? minute : end };
      }
    }
    day -= 1;
    minute = MINUTES_PER_DAY;
  }
  return at;
}

/** 將時間點對齊到下一個工作時刻（已在工作時間內則原值回傳）。 */
export function snapToNextWorkingTime(
  calendar: ResolvedCalendar,
  at: ISODateTime,
): ISODateTime {
  const from = parseDateTime(at);
  const point = nextWorkingPoint(calendar, dayLookup(calendar), from);
  if (point.day === from.day && point.minute === from.minute) return at;
  return formatDateTime(point.day, point.minute);
}

/** 將時間點對齊到上一個工作時刻（已在工作時間內則原值回傳）。 */
export function snapToPreviousWorkingTime(
  calendar: ResolvedCalendar,
  at: ISODateTime,
): ISODateTime {
  const from = parseDateTime(at);
  const point = previousWorkingPoint(calendar, dayLookup(calendar), from);
  if (point.day === from.day && point.minute === from.minute) return at;
  return formatDateTime(point.day, point.minute);
}

// ---------------------------------------------------------------------------
// 位移
// ---------------------------------------------------------------------------

function assertDuration(duration: Minutes, fnName: string): number {
  const value = duration as number;
  if (!Number.isFinite(value) || value < 0) {
    throw new InvalidArgumentError(`${fnName}: duration must be a finite non-negative number of minutes`);
  }
  return value;
}

/** 自 `from` 起，往後推進 `duration` 個「工作分鐘」後的時間點。 */
export function addWorkingMinutes(
  calendar: ResolvedCalendar,
  from: ISODateTime,
  duration: Minutes,
): ISODateTime {
  let remaining = assertDuration(duration, 'addWorkingMinutes()');
  if (remaining === 0) return from;

  const lookup = dayLookup(calendar);
  const start = nextWorkingPoint(
    calendar,
    lookup,
    canonicalizeInstant(lookup, parseDateTime(from)),
  );
  let day = start.day;
  let minute = start.minute;

  for (let guard = 0; guard <= MAX_NON_WORKING_DAYS; guard += 1) {
    for (const segment of lookup(day).segments) {
      const segStart = segment.start as number;
      const segEnd = segment.end as number;
      if (segEnd <= minute) continue;
      const cursor = minute > segStart ? minute : segStart;
      const available = segEnd - cursor;
      // remaining === available 時停在時段結束點（finish 為含端點的邊界）。
      if (remaining <= available) return formatDateTime(day, cursor + remaining);
      remaining -= available;
      minute = segEnd;
    }
    day += 1;
    minute = 0;
    guard = lookup(day).total > 0 ? 0 : guard;
  }
  throw new InvalidCalendarError(
    calendar.id as string,
    `no working time found within ${MAX_NON_WORKING_DAYS} days while adding ${duration as number} minutes`,
  );
}

/** 自 `from` 起，往前回推 `duration` 個「工作分鐘」後的時間點。 */
export function subtractWorkingMinutes(
  calendar: ResolvedCalendar,
  from: ISODateTime,
  duration: Minutes,
): ISODateTime {
  let remaining = assertDuration(duration, 'subtractWorkingMinutes()');
  if (remaining === 0) return from;

  const lookup = dayLookup(calendar);
  const start = previousWorkingPoint(
    calendar,
    lookup,
    canonicalizeInstant(lookup, parseDateTime(from)),
  );
  let day = start.day;
  let minute = start.minute;

  for (let guard = 0; guard <= MAX_NON_WORKING_DAYS; guard += 1) {
    const segments = lookup(day).segments;
    for (let i = segments.length - 1; i >= 0; i -= 1) {
      const segment = segments[i];
      if (segment === undefined) continue;
      const segStart = segment.start as number;
      const segEnd = segment.end as number;
      if (segStart >= minute) continue;
      const cursor = minute < segEnd ? minute : segEnd;
      const available = cursor - segStart;
      if (remaining <= available) return formatDateTime(day, cursor - remaining);
      remaining -= available;
      minute = segStart;
    }
    day -= 1;
    minute = MINUTES_PER_DAY;
    guard = lookup(day).total > 0 ? 0 : guard;
  }
  throw new InvalidCalendarError(
    calendar.id as string,
    `no working time found within ${MAX_NON_WORKING_DAYS} days while subtracting ${duration as number} minutes`,
  );
}

// ---------------------------------------------------------------------------
// 區間加總
// ---------------------------------------------------------------------------

function overlapMinutes(info: DayInfo, low: number, high: number): number {
  if (low <= 0 && high >= MINUTES_PER_DAY) return info.total;
  let total = 0;
  for (const segment of info.segments) {
    const start = segment.start as number;
    const end = segment.end as number;
    const lo = start > low ? start : low;
    const hi = end < high ? end : high;
    if (hi > lo) total += hi - lo;
  }
  return total;
}

function sumWorkingMinutes(
  calendar: ResolvedCalendar,
  lookup: (day: number) => DayInfo,
  from: DayMinute,
  to: DayMinute,
): number {
  if (to.day - from.day > MAX_SPAN_DAYS) {
    throw new InvalidArgumentError(
      `workingMinutesBetween(): range on calendar ${calendar.id as string} spans more than ` +
        `${MAX_SPAN_DAYS} days; refusing to iterate`,
    );
  }
  let total = 0;
  let day = from.day;
  let minute = from.minute;
  while (day < to.day) {
    total += overlapMinutes(lookup(day), minute, MINUTES_PER_DAY);
    day += 1;
    minute = 0;
  }
  if (to.minute > minute) {
    total += overlapMinutes(lookup(day), minute, to.minute);
  }
  return total;
}

/**
 * 計算兩個時間點之間的工作分鐘數（`from` 含、`to` 不含）。
 *
 * 有號語義：`from > to` 時回傳負值（絕對值等同於 `workingMinutesBetween(to, from)`），
 * 供 backward pass 直接使用而不必先排序兩端點；`from === to` 回傳 0。
 */
export function workingMinutesBetween(
  calendar: ResolvedCalendar,
  from: ISODateTime,
  to: ISODateTime,
): Minutes {
  const lookup = dayLookup(calendar);
  const a = parseDateTime(from);
  const b = parseDateTime(to);
  if (a.day === b.day && a.minute === b.minute) return minutes(0);
  if (a.day > b.day || (a.day === b.day && a.minute > b.minute)) {
    return minutes(-sumWorkingMinutes(calendar, lookup, b, a));
  }
  return minutes(sumWorkingMinutes(calendar, lookup, a, b));
}
